import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import { adminApi, CreditAnalytics, CreditTransaction, CreditTransactionsResponse, SubscriptionAnalytics } from '../../services/api';
import { ChevronLeft, ChevronRight, Zap, TrendingUp, Users, DollarSign, CreditCard, Activity } from 'lucide-react';

export const CreditUsageDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  
  const [creditAnalytics, setCreditAnalytics] = useState<CreditAnalytics | null>(null);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState<SubscriptionAnalytics | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [transactionsResponse, setTransactionsResponse] = useState<CreditTransactionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(30);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);

  const checkAdminAccess = async () => {
    try {
      await adminApi.getSystemStats();
      setIsAdmin(true);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setIsAdmin(false);
      } else {
        setIsAdmin(true);
      }
    }
  };

  const fetchCreditData = async () => {
    try {
      setLoading(true);
      
      const offset = (currentPage - 1) * pageSize;
      
      const [creditData, subscriptionData, transactionData] = await Promise.all([
        adminApi.getCreditAnalytics(selectedPeriod),
        adminApi.getSubscriptionAnalytics(),
        adminApi.getCreditTransactions({
          limit: pageSize,
          offset: offset
        })
      ]);
      
      setCreditAnalytics(creditData);
      setSubscriptionAnalytics(subscriptionData);
      setTransactionsResponse(transactionData);
      setTransactions(transactionData.transactions);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setIsAdmin(false);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load credit data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeComponent = async () => {
      await checkAdminAccess();
    };
    initializeComponent();
  }, []);

  useEffect(() => {
    if (isAdmin === true) {
      setCurrentPage(1);
    }
  }, [selectedPeriod, isAdmin]);

  useEffect(() => {
    if (isAdmin === true) {
      fetchCreditData();
    }
  }, [currentPage, selectedPeriod, isAdmin]);

  // Show loading while checking admin status
  if (isAdmin === null) {
    return <LoadingSpinner />;
  }

  // Show error message if user is not admin
  if (isAdmin === false) {
    return <ErrorMessage />;
  }

  const formatCredits = (credits: number) => credits.toLocaleString(isRTL ? 'he-IL' : 'en-US', { maximumFractionDigits: 2 });
  const formatMoney = (amount: number) => new Intl.NumberFormat(isRTL ? 'he-IL' : 'en-US', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(isRTL ? 'he-IL' : 'en-US', {
      timeZone: 'Asia/Jerusalem',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Pagination calculations
  const totalPages = transactionsResponse ? Math.ceil(transactionsResponse.total_count / pageSize) : 0;
  const startItem = transactionsResponse ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = transactionsResponse ? Math.min(currentPage * pageSize, transactionsResponse.total_count) : 0;

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="text-center">
        <div className="text-vibrant-orange text-xl mb-2">Error</div>
        <div className="text-surface-secondary">{error}</div>
      </div>
    </div>
  );

  if (!creditAnalytics || !subscriptionAnalytics) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="text-center">
        <div className="text-surface-primary text-xl mb-2">No Data</div>
        <div className="text-surface-secondary">No credit data available</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-6 max-w-none w-full">
      <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
        <h2 className="text-3xl font-bold text-bg-secondary font-hebrew">
          ⚡ {t('admin:credit_analytics.title', 'Credit Analytics')}
        </h2>
        <div className={`flex gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="bg-white text-surface-primary px-4 py-2 rounded-lg border border-surface-muted/30 focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all font-hebrew"
          >
            <option value={7}>{t('admin:periods.last_7_days', 'Last 7 days')}</option>
            <option value={30}>{t('admin:periods.last_30_days', 'Last 30 days')}</option>
            <option value={90}>{t('admin:periods.last_90_days', 'Last 90 days')}</option>
          </select>
          <button
            onClick={fetchCreditData}
            className="bg-vibrant-orange text-white px-4 py-2 rounded-lg hover:bg-vibrant-orange/90 transition-all duration-200 text-sm font-medium font-hebrew"
          >
            {t('common:actions.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} mb-3`}>
            <div className="w-12 h-12 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-vibrant-orange" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <h3 className="text-sm font-medium text-surface-secondary font-hebrew">
                {t('admin:credit_analytics.credits_used', 'Credits Used')} ({selectedPeriod}d)
              </h3>
              <p className="text-2xl font-bold text-surface-primary">{formatCredits(creditAnalytics.total_credits_used)}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} mb-3`}>
            <div className="w-12 h-12 bg-gradient-to-br from-muted-purple/10 to-muted-purple/20 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-muted-purple" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <h3 className="text-sm font-medium text-surface-secondary font-hebrew">
                {t('admin:credit_analytics.credits_purchased', 'Credits Purchased')} ({selectedPeriod}d)
              </h3>
              <p className="text-2xl font-bold text-surface-primary">{formatCredits(creditAnalytics.total_credits_purchased)}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} mb-3`}>
            <div className="w-12 h-12 bg-gradient-to-br from-green-500/10 to-green-500/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-500" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <h3 className="text-sm font-medium text-surface-secondary font-hebrew">
                {t('admin:credit_analytics.revenue_estimate', 'Revenue Estimate')} ({selectedPeriod}d)
              </h3>
              <p className="text-2xl font-bold text-vibrant-orange">{formatMoney(creditAnalytics.revenue_estimate_ils)}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} mb-3`}>
            <div className="w-12 h-12 bg-gradient-to-br from-galaxy-pink/10 to-galaxy-pink/20 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-galaxy-pink" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <h3 className="text-sm font-medium text-surface-secondary font-hebrew">
                {t('admin:credit_analytics.total_transactions', 'Total Transactions')} ({selectedPeriod}d)
              </h3>
              <p className="text-2xl font-bold text-surface-primary">{creditAnalytics.total_transactions.toLocaleString()}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts and Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage by Action */}
        <Card className="p-6">
          <h3 className={`text-xl font-semibold text-surface-primary mb-4 font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {t('admin:credit_analytics.usage_by_action', 'Usage by Action')}
          </h3>
          <div className="space-y-3">
            {Object.entries(creditAnalytics.usage_by_action).map(([action, data]) => (
              <div key={action} className={`flex justify-between items-center p-3 rounded-lg bg-surface-primary/30 hover:bg-surface-primary/50 transition-all ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : ''}>
                  <span className="text-surface-primary capitalize font-medium font-hebrew">
                    {action.replace('_', ' ')}
                  </span>
                  <div className="text-xs text-surface-secondary font-hebrew">
                    {data.transaction_count} {t('admin:credit_analytics.transactions', 'transactions')}
                  </div>
                </div>
                <div className={`text-vibrant-orange font-bold font-hebrew ${isRTL ? 'text-left' : 'text-right'}`}>
                  {formatCredits(data.total_credits)}
                </div>
              </div>
            ))}
          </div>
        </Card>
        
        {/* User Tier Distribution */}
        <Card className="p-6">
          <h3 className={`text-xl font-semibold text-surface-primary mb-4 font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {t('admin:credit_analytics.tier_distribution', 'User Tier Distribution')}
          </h3>
          <div className="space-y-3">
            {Object.entries(subscriptionAnalytics.tier_totals).map(([tier, count]) => (
              <div key={tier} className={`flex justify-between items-center p-3 rounded-lg bg-surface-primary/30 hover:bg-surface-primary/50 transition-all ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="text-surface-primary font-medium font-hebrew capitalize">
                  {t(`credits:tiers.${tier}`, tier)}
                </span>
                <span className="text-muted-purple font-bold font-hebrew">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Top Credit Consumers */}
      <Card className="p-6">
        <h3 className={`text-xl font-semibold text-surface-primary mb-4 font-hebrew ${isRTL ? 'text-right' : ''}`}>
          {t('admin:credit_analytics.top_consumers', 'Top Credit Consumers')}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-muted/30">
                <th className={`py-3 text-surface-secondary font-semibold font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admin:labels.user', 'User')}
                </th>
                <th className={`py-3 text-surface-secondary font-semibold font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admin:labels.tier', 'Tier')}
                </th>
                <th className={`py-3 text-surface-secondary font-semibold font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admin:labels.credits_used', 'Credits Used')}
                </th>
              </tr>
            </thead>
            <tbody>
              {creditAnalytics.top_consumers.map((consumer, index) => (
                <tr key={index} className="border-b border-surface-muted/20 hover:bg-surface-primary/20 transition-colors">
                  <td className={`py-3 text-surface-primary font-medium font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    {consumer.email}
                  </td>
                  <td className={`py-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      consumer.tier === 'unlimited' 
                        ? 'bg-vibrant-orange/20 text-vibrant-orange border border-vibrant-orange/30'
                        : consumer.tier === 'premium'
                        ? 'bg-muted-purple/20 text-muted-purple border border-muted-purple/30'
                        : 'bg-surface-muted/20 text-surface-primary border border-surface-muted/30'
                    }`}>
                      {t(`credits:tiers.${consumer.tier}`, consumer.tier)}
                    </span>
                  </td>
                  <td className={`py-3 text-vibrant-orange font-bold font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    {formatCredits(consumer.credits_used)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent Credit Transactions */}
      <Card className="p-6">
        <div className={`flex justify-between items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <h3 className={`text-xl font-semibold text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {t('admin:credit_analytics.recent_transactions', 'Recent Credit Transactions')}
            {transactionsResponse && (
              <span className="text-sm text-surface-secondary font-normal">
                ({transactionsResponse.total_count.toLocaleString()} {t('admin:labels.total', 'total')})
              </span>
            )}
          </h3>
        </div>

        {/* Pagination Info */}
        {transactionsResponse && transactionsResponse.total_count > 0 && (
          <div className={`flex justify-between items-center mb-4 text-sm text-surface-secondary font-hebrew ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div>
              {t('admin:pagination.showing', 'Showing')} {startItem}-{endItem} {t('admin:pagination.of', 'of')} {transactionsResponse.total_count.toLocaleString()} {t('admin:labels.transactions', 'transactions')}
            </div>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{t('admin:pagination.page', 'Page')} {currentPage} {t('admin:pagination.of', 'of')} {totalPages}</span>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-muted/30">
                <th className={`py-3 text-surface-secondary font-semibold font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admin:labels.time', 'Time')}
                </th>
                <th className={`py-3 text-surface-secondary font-semibold font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admin:labels.user', 'User')}
                </th>
                <th className={`py-3 text-surface-secondary font-semibold font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admin:labels.action', 'Action')}
                </th>
                <th className={`py-3 text-surface-secondary font-semibold font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admin:labels.credits', 'Credits')}
                </th>
                <th className={`py-3 text-surface-secondary font-semibold font-hebrew ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admin:labels.amount', 'Amount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <tr key={transaction.id} className="border-b border-surface-muted/20 hover:bg-surface-primary/20 transition-colors">
                  <td className={`py-3 text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    {formatDate(transaction.created_at)}
                  </td>
                  <td className={`py-3 text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    {transaction.user_email}
                  </td>
                  <td className={`py-3 text-surface-primary font-medium font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    {transaction.action_type.replace(/_/g, ' ')}
                  </td>
                  <td className={`py-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    {transaction.credits_used > 0 && (
                      <span className="text-red-500 font-medium">-{formatCredits(transaction.credits_used)}</span>
                    )}
                    {transaction.credits_added > 0 && (
                      <span className="text-green-500 font-medium">+{formatCredits(transaction.credits_added)}</span>
                    )}
                  </td>
                  <td className={`py-3 text-vibrant-orange font-bold font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    {transaction.amount_ils ? formatMoney(transaction.amount_ils) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {transactionsResponse && totalPages > 1 && (
          <div className={`flex justify-between items-center mt-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all font-hebrew ${
                  currentPage === 1
                    ? 'bg-surface-muted/20 text-surface-secondary cursor-not-allowed'
                    : 'bg-white text-surface-primary border border-surface-muted/30 hover:bg-surface-primary/10 hover:text-vibrant-orange'
                }`}
              >
                {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                {t('admin:pagination.previous', 'Previous')}
              </button>
              
              <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {getVisiblePages().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === '...' ? (
                      <span className="px-3 py-2 text-surface-secondary">...</span>
                    ) : (
                      <button
                        onClick={() => goToPage(page as number)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all font-hebrew ${
                          currentPage === page
                            ? 'bg-vibrant-orange text-white'
                            : 'bg-white text-surface-primary border border-surface-muted/30 hover:bg-surface-primary/10 hover:text-vibrant-orange'
                        }`}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </div>
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all font-hebrew ${
                  currentPage === totalPages
                    ? 'bg-surface-muted/20 text-surface-secondary cursor-not-allowed'
                    : 'bg-white text-surface-primary border border-surface-muted/30 hover:bg-surface-primary/10 hover:text-vibrant-orange'
                }`}
              >
                {t('admin:pagination.next', 'Next')}
                {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
            
            <div className="text-sm text-surface-secondary font-hebrew">
              {pageSize} {t('admin:labels.per_page', 'per page')}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};